import { SubmitHandler, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import {
	CreateCompanyTaxReliefDto,
	PayrollComponent,
} from "../../../../../../models/tax-relief.dto";
import {
	APP_CONFIG,
	enumToStringConverter,
	formatRate,
	IAddEditTableForm,
} from "../../../../../../helpers/appHelpers";
import useEditFormStore from "../../../../../../state-management/useEditFormStore";
import {
	useCreateCompanyTaxRelief,
	useUpdateCompanyTaxRelief,
} from "../../../../../../services/company-tax-relief.service";
import ModalComponent from "../../../../../../components/modals/ModalFormComponent";
import Label from "../../../../../../components/form_fields/Label";
import MoneyInput from "../../../../../../components/form_fields/MoneyInput";
import { useGetTaxReliefType } from "../../../../../../services/tax-relief-type.service";
import { useManageTableWithSearchParamsData } from "../../../../../../helpers/useManageTable";
import RateComponent from "../../../../../../components/form_fields/RateComponent";
import APISelectField from "../../../../../../components/form_fields/APISelectField";
import { PATHS } from "../../../../../../routes/routes.paths";

interface IFormInputs extends CreateCompanyTaxReliefDto {}

type ICompanyReliefTaxRuleModalForm = IAddEditTableForm & {
	companyId: number;
	reloadTableData?: () => void;
};

const CompanyReliefTaxRuleModalForm = ({
	open,
	setOpen,
	title,
	isEditForm,
	tableAPIRef,
	companyId,
	reloadTableData,
}: ICompanyReliefTaxRuleModalForm) => {
	const { formDataValues } = useEditFormStore();
	const [uploading, setUploading] = useState(false);

	const {
		register,
		handleSubmit,
		watch,
		setValue,
		reset,
		formState: { errors },
	} = useForm<IFormInputs>({
		defaultValues: {
			rateComponent: isEditForm
				? ((Number(formDataValues?.rateComponent) * 100) as any)
				: "",
			fixedComponent: isEditForm ? formDataValues?.fixedComponent : "",
			taxReliefTypeId: isEditForm ? formDataValues?.taxReliefTypeId : "",
			incomePeriod: isEditForm ? formDataValues?.incomePeriod : "",
			amountDerivedFrom: isEditForm ? formDataValues?.amountDerivedFrom : "",
			benchmarkRateComponent: isEditForm
				? ((Number(formDataValues?.benchmarkRateComponent) * 100) as any)
				: "",
			benchmarkFixedComponent: isEditForm
				? formDataValues?.benchmarkFixedComponent
				: "",
		},
	});

	const { data: taxReliefTypes, isLoading: taxReliefTypesLoading } =
		useGetTaxReliefType({
			limit: APP_CONFIG.PAGE_LIMIT,
			orderBy: "createdAt:desc" as any,
		});

	useEffect(() => {
		setValue(
			"taxReliefTypeId",
			isEditForm ? formDataValues?.taxReliefTypeId : "",
		);
	}, [taxReliefTypesLoading]);

	const { mutate: createTaxRelief } = useCreateCompanyTaxRelief();
	const { mutate: updateTaxRelief } = useUpdateCompanyTaxRelief();
	const { addNewTableData } = useManageTableWithSearchParamsData();

	const onSubmit: SubmitHandler<IFormInputs> = (data: any) => {
		setUploading(true);
		formatRate(data);
		formatRate(data, "benchmarkRateComponent");

		if (isEditForm) {
			updateTaxRelief(
				{ companyId, id: formDataValues?.id, payload: data },
				{
					onSuccess: (updatedData) => {
						try {
							tableAPIRef?.current.updateRows([updatedData.data]);
						} catch (error) {
							console.error("Error updating table rows:", error);
							reloadTableData?.();
						}
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				},
			);
		} else {
			createTaxRelief(
				{ companyId, payload: data },
				{
					onSuccess: () => {
						addNewTableData();
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				},
			);
		}
	};

	const handleClose = () => {
		setOpen(false);
		reset();
	};

	return (
		<ModalComponent
			width={775}
			open={open}
			title={title}
			handleClose={handleClose}
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="grid grid-cols-2 gap-x-4 gap-y-6">
					<div className="w-full">
						<Label title="Tax Relief Type" for="taxReliefTypeId" />
						<APISelectField
							key="taxReliefTypeId"
							attributes={{ id: "taxReliefTypeId" }}
							placeholder="Select Type"
							register={register}
							name="taxReliefTypeId"
							options={taxReliefTypes?.data || []}
							pathname={PATHS.ADMIN_SUB_SYSTEM_CONFIGURATIONS.TAX_RELIEF_TYPES}
							required="Select tax relief type here"
							error={errors.taxReliefTypeId}
							valueAsNumber
						/>
						{errors.taxReliefTypeId && (
							<p className="text-red-500 text-sm mt-1">
								{errors.taxReliefTypeId.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Income Period" for="incomePeriod" />
						<select
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm mb-2 ${
								errors.incomePeriod
									? "border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
							{...register("incomePeriod", {
								required: "Select income period here",
							})}
						>
							<option value="">Select Period</option>
							<option value="MONTH">Month</option>
							<option value="YEAR">Year</option>
						</select>
						{errors.incomePeriod && (
							<p className="text-red-500 text-sm mt-1">
								{errors.incomePeriod.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Rate Component" for="rateComponent" />
						<RateComponent
							attributes={{ id: "rateComponent" }}
							register={register}
							name="rateComponent"
							required="Enter rate component"
							error={errors.rateComponent}
						/>
						{errors.rateComponent && (
							<p className="text-red-500 text-sm mt-1">
								{errors.rateComponent.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Fixed Component" for="fixedComponent" optional />
						<MoneyInput
							attributes={{ id: "fixedComponent", placeholder: "0" }}
							register={register}
							name="fixedComponent"
							setValue={setValue}
							defaultValue={
								isEditForm ? formDataValues?.fixedComponent : undefined
							}
							required={false}
							error={errors.fixedComponent}
						/>
					</div>
					<div className="w-full">
						<Label title="Amount Based On" for="amountDerivedFrom" optional />
						<select
							className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm mb-2 ${
								errors.amountDerivedFrom
									? "border border-red-500 focus:border-red-500 focus:outline-red-500"
									: "border-none"
							}`}
							{...register("amountDerivedFrom", { required: false })}
						>
							<option value="">Select Option</option>
							{Object.entries(PayrollComponent).map(([key, value]) => (
								<option key={key} value={value}>
									{enumToStringConverter(value)}
								</option>
							))}
						</select>
					</div>
					<div className="w-full">
						<Label
							title="Benchmark Rate Component"
							for="benchmarkRateComponent"
							optional
						/>
						<RateComponent
							attributes={{ id: "benchmarkRateComponent" }}
							register={register}
							name="benchmarkRateComponent"
							required={false}
							error={errors.benchmarkRateComponent}
						/>
					</div>
					<div className="w-full">
						<Label
							title="Benchmark Fixed Component"
							for="benchmarkFixedComponent"
							optional
						/>
						<MoneyInput
							attributes={{ id: "benchmarkFixedComponent", placeholder: "0" }}
							register={register}
							name="benchmarkFixedComponent"
							setValue={setValue}
							defaultValue={
								isEditForm ? formDataValues?.benchmarkFixedComponent : undefined
							}
							required={false}
							error={errors.benchmarkFixedComponent}
						/>
					</div>
				</div>

				<div className="w-full flex justify-end mt-8">
					<button
						type="submit"
						disabled={uploading}
						className="bg-primary-mango text-white h-14 px-8 rounded-lg text-sm"
					>
						{uploading
							? "Uploading..."
							: isEditForm
								? "Update Relief Rule"
								: "Add Relief Rule"}
					</button>
				</div>
			</form>
		</ModalComponent>
	);
};

export default CompanyReliefTaxRuleModalForm;
